import { lazy, Suspense, useState } from "react";
import "./App.css";
import { Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "./components/landing/LandingPage";
import { getLang } from "./i18n";

// Only the landing page ships in the main bundle; everything else loads on demand.
const Sidebar = lazy(() => import("./components/Sidebar"));
const Header = lazy(() => import("./components/Header"));
const DashboardHome = lazy(() => import("./components/DashboardHome"));
const SessionStatus = lazy(() => import("./components/session/SessionStatus"));
const ChatPage = lazy(() => import("./components/chat/ChatPage"));
const AutoReplyPage = lazy(() => import("./components/autoreply/AutoReplyPage"));
const ProfilePage = lazy(() => import("./components/ProfilePage"));
const SendMessagePage = lazy(() => import("./components/chat/SendMessagePage"));
const LoginPage = lazy(() => import("./components/auth/LoginPage"));
const RegisterPage = lazy(() => import("./components/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./components/auth/ForgotPasswordPage"));
const ClientPage = lazy(() => import("./components/ClientPage"));
const GroupPage = lazy(() => import("./components/GroupPage"));
const NotFoundPage = lazy(() => import("./components/NotFoundPage"));
const UserManagementPage = lazy(() => import("./components/superadmin/UserManagementPage"));
const ApiDocsCenter = lazy(() => import("./components/ApiDocsCenter"));
const InboxPage = lazy(() => import("./components/saas/InboxPage"));
const GrowthPage = lazy(() => import("./components/saas/GrowthPage"));
const AutomationStudio = lazy(() => import("./components/saas/AutomationStudio"));
const WorkspaceSuite = lazy(() => import("./components/saas/WorkspaceSuite"));

function RequireAuth({ children }) {
  const token = localStorage.getItem("access_token");
  if (!token) {
    return <Navigate to="/auth/login" replace />;
  }
  return children;
}

function RequireSuperadmin({ children }) {
  if (localStorage.getItem("role") !== "superadmin") {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    window.matchMedia("(min-width: 1024px)").matches
  );

  const handleSidebarToggle = () => setSidebarOpen((open) => !open);

  const lang = getLang();
  const isRTL = lang === "ar";
  const sidebarMargin = isRTL
    ? sidebarOpen
      ? "lg:mr-72"
      : "lg:mr-20"
    : sidebarOpen
    ? "lg:ml-72"
    : "lg:ml-20";
  return (
    <div
      className="min-h-screen bg-slate-50 text-slate-900"
      dir={isRTL ? "rtl" : "ltr"}
      lang={lang}
    >
      <Suspense fallback={null}>
      <Routes>
        <Route
          path="/auth/login"
          element={
            <main className="flex-1 p-0 bg-gray-100">
              <LoginPage />
            </main>
          }
        />

        <Route
          path="/auth/register"
          element={
            <main className="flex-1 p-0 bg-gray-100">
              <RegisterPage />
            </main>
          }
        />
        <Route
          path="/auth/forgot-password"
          element={
            <main className="min-h-screen bg-slate-50">
              <ForgotPasswordPage />
            </main>
          }
        />
        <Route path="/" element={<LandingPage />} />

        {/* Change: make admin area only match /admin/* so global 404 can render without header/sidebar */}
        <Route
          path="/admin/*"
          element={
            <RequireAuth>
              <>
                {sidebarOpen && (
                  <button
                    type="button"
                    className="fixed inset-0 z-30 bg-slate-950/35 backdrop-blur-[1px] lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                    aria-label={isRTL ? "إغلاق القائمة" : "Close navigation"}
                  />
                )}
                <Sidebar
                  open={sidebarOpen}
                  onToggle={handleSidebarToggle}
                  onNavigate={() => {
                    if (window.innerWidth < 1024) setSidebarOpen(false);
                  }}
                />
                <div
                  className={`flex min-h-screen flex-col transition-[margin] duration-300 ${sidebarMargin}`}
                >
                  <Header
                    onSidebarToggle={handleSidebarToggle}
                    sidebarOpen={sidebarOpen}
                  />
                  <main id="main-content" className="flex-1 px-4 py-5 sm:px-6 lg:px-8 lg:py-7">
                    <Suspense fallback={null}>
                    <Routes>
                      <Route index element={<DashboardHome />} />
                      <Route path="dashboard" element={<Navigate to="/admin" replace />} />
                      <Route path="session" element={<SessionStatus />} />
                      <Route
                        path="chat"
                        element={
                          <ChatPage
                            onSidebarToggle={handleSidebarToggle}
                            sidebarOpen={sidebarOpen}
                          />
                        }
                      />
                      <Route path="auto-reply" element={<AutoReplyPage />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="send-message" element={<SendMessagePage />} />
                      <Route path="client" element={<ClientPage />} />
                      <Route path="groups" element={<GroupPage />} />
                      <Route path="api-docs" element={<ApiDocsCenter />} />
                      <Route path="inbox" element={<InboxPage />} />
                      <Route path="growth" element={<GrowthPage />} />
                      <Route path="automation-studio" element={<AutomationStudio />} />
                      <Route path="workspace" element={<WorkspaceSuite />} />
                      <Route
                        path="users"
                        element={
                          <RequireSuperadmin>
                            <UserManagementPage />
                          </RequireSuperadmin>
                        }
                      />
                      <Route path="*" element={<NotFoundPage compact />} />
                    </Routes>
                    </Suspense>
                  </main>
                </div>
              </>
            </RequireAuth>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </div>
  );
}

export default App;
